import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  FolderKanban,
  Users,
  ClipboardList,
  ScrollText,
  LogOut,
  LayoutDashboard,
  Search,
  Zap,
} from "lucide-react";
import { Toaster } from "sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, adminOnly: false },
  { to: "/accounts", label: "Accounts", icon: Building2, adminOnly: true },
  { to: "/projects", label: "Projects", icon: FolderKanban, adminOnly: false },
  { to: "/messages", label: "Messages", icon: Search, adminOnly: false },
  { to: "/users", label: "Users", icon: Users, adminOnly: true },
  { to: "/audit", label: "Audit", icon: ScrollText, adminOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <Toaster position="top-right" richColors />
      <aside className="w-56 border-r bg-card flex flex-col" data-testid="app-sidebar">
        <div className="px-4 py-5 border-b">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded bg-foreground text-background flex items-center justify-center text-xs font-bold">
              SC
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight">SMSCraft</div>
              <div className="text-[10px] text-muted-foreground">
                SMS orchestrator
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-1">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-accent text-accent-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-3 space-y-2">
          <div className="text-xs">
            <div className="font-medium truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Badge
            variant={user?.role === "admin" ? "default" : "secondary"}
            className="text-[10px]"
            data-testid="sidebar-user-role"
          >
            {user?.role}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleLogout}
            data-testid="sidebar-logout"
          >
            <LogOut className="mr-2 h-3 w-3" />
            Logout
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto flex flex-col">
        <div className="sticky top-0 z-30 h-14 border-b bg-background/80 backdrop-blur flex items-center justify-end gap-3 px-6" data-testid="app-topbar">
          <Button asChild size="sm" className="shadow-sm" data-testid="topbar-quicksend">
            <Link to="/quick-send">
              <Zap className="h-4 w-4 mr-1.5" />Quick Send
            </Link>
          </Button>
        </div>
        <div className="flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
